import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { downloadMediaFile } from '@/common/utils/download';
import { useBodyScrollLock } from '@/common/hooks';
import { ZoomPanImage } from './ZoomPanImage';
import React, { Suspense } from 'react';
import { getMediaFileType } from './utils';
import { resolveWhatsAppMediaUrl } from '@/common/utils/resolveMediaUrl';
import { MediaFileType } from './constants';

const PdfPreview = React.lazy(() => import('./previews/PdfPreview'));
const DocxPreview = React.lazy(() => import('./previews/DocxPreview'));
const CsvPreview = React.lazy(() => import('./previews/CsvPreview'));
const SpreadsheetPreview = React.lazy(
  () => import('./previews/SpreadsheetPreview'),
);
const TextPreview = React.lazy(() => import('./previews/TextPreview'));

export function MediaViewerModal({
  isOpen,
  onClose,
  mediaList = [],
  initialIndex = 0,
  contactName = '',
  contactTime = '',
  surface = '',
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const carouselRef = useRef(null);
  const thumbnailRefs = useRef([]);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setZoom(1);
  }, [initialIndex, isOpen]);

  // Auto-scroll the thumbnail strip to keep the active thumbnail visible
  useEffect(() => {
    if (!isOpen || !thumbnailRefs.current[currentIndex]) return;
    const timer = setTimeout(() => {
      const activeThumb = thumbnailRefs.current[currentIndex];
      const container = carouselRef.current;
      if (activeThumb && container) {
        const containerRect = container.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        const currentScrollLeft = container.scrollLeft;
        const targetScrollLeft =
          currentScrollLeft +
          (thumbRect.left - containerRect.left) -
          containerRect.width / 2 +
          thumbRect.width / 2;

        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth',
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [currentIndex, isOpen]);

  useBodyScrollLock(isOpen);

  const handlePrev = useCallback(() => {
    setZoom(1);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
  }, [mediaList.length]);

  const handleNext = useCallback(() => {
    setZoom(1);
    setCurrentIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
  }, [mediaList.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrev, handleNext, onClose]);

  // Touch and mouse drag swipe handlers for left/right navigation
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const isDragging = useRef(false);
  const mouseStartX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 40;

    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('audio, video, iframe, button, a')) return;
    isDragging.current = true;
    mouseStartX.current = e.clientX;
  };

  const handleMouseUp = (e) => {
    if (!isDragging.current || mouseStartX.current === null) return;
    const distance = mouseStartX.current - e.clientX;
    const minSwipeDistance = 40;

    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }

    isDragging.current = false;
    mouseStartX.current = null;
  };

  if (!isOpen || !mediaList.length) return null;

  const currentMedia = mediaList[currentIndex] || mediaList[0];
  const currentMediaUrl = resolveWhatsAppMediaUrl(currentMedia?.mediaUrl || '');

  const handleDownload = async () => {
    if (!currentMedia?.mediaUrl) return;
    const url = resolveWhatsAppMediaUrl(currentMedia.mediaUrl);
    const fileName = currentMedia.fileName || 'download';
    const loadingToastId = toast.loading('Downloading file...');

    try {
      await downloadMediaFile(url, fileName);
      toast.success('Download complete!', { id: loadingToastId });
    } catch (err) {
      console.error('Download failed: ', err);
      toast.error('Download failed', { id: loadingToastId });
    }
  };

  const mediaType = getMediaFileType(
    currentMedia.mediaUrl,
    currentMedia.fileName,
    currentMedia.mimeType || currentMedia.messageType,
  );

  const isImage = mediaType === MediaFileType.IMAGE;
  const isVideo = mediaType === MediaFileType.VIDEO;
  const isAudio = mediaType === MediaFileType.AUDIO;
  const isPdf = mediaType === MediaFileType.PDF;
  const isDocx = mediaType === MediaFileType.DOCX;
  const isExcel = mediaType === MediaFileType.EXCEL;
  const isCsv = mediaType === MediaFileType.CSV;
  const isText = mediaType === MediaFileType.TEXT;

  const isWhatsAppInbox = surface === 'whatsapp-inbox';

  const viewer = (
    <div
      data-modal-root
      className={`fixed inset-0 z-[9999] bg-[#111b21] flex flex-col justify-between overflow-hidden select-none animate-fadeIn${
        isWhatsAppInbox ? ' wa-media-viewer w-screen h-dvh' : ''
      }`}
      style={isWhatsAppInbox ? { width: '100vw', height: '100dvh' } : undefined}
    >
      {/* Top Bar Header */}
      <div className="wa-media-viewer-header flex items-center justify-between px-4 py-3 bg-[#111b21] text-gray-200 border-b border-gray-800/60 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center text-white font-semibold">
            {contactName ? contactName.charAt(0).toUpperCase() : 'C'}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white leading-tight truncate max-w-md">
              {contactName}
            </h4>
            <p className="text-xs text-gray-400 mt-0.5">
              {currentMedia?.time || contactTime || 'Today'}
            </p>
          </div>
        </div>

        {/* Top Control Action Icons */}
        <div className="flex items-center gap-4 text-gray-300">
          {/* Download button */}
          <button
            onClick={handleDownload}
            className="p-1.5 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            title="Download"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 hover:text-white hover:bg-white/10 rounded-full transition-colors ml-2"
            title="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content Stage */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        className={`wa-media-viewer-stage relative flex-1 flex items-center justify-center min-h-0 overflow-hidden cursor-grab active:cursor-grabbing select-none ${
          isPdf ? 'p-0' : 'p-4'
        }`}
      >
        {/* Media Preview Renderer */}
        <div className="w-full h-full flex items-center justify-center">
          {isImage ? (
            <ZoomPanImage
              src={currentMediaUrl}
              alt={currentMedia.fileName || 'Media'}
              zoom={zoom}
              onZoomChange={setZoom}
              onDownload={handleDownload}
            />
          ) : isVideo ? (
            <video
              src={currentMediaUrl}
              controls
              className="max-h-full max-w-full rounded shadow-2xl"
            />
          ) : isAudio ? (
            <div className="p-6 bg-[#202c33] rounded-3xl flex flex-col items-center gap-6 shadow-2xl min-w-[360px] max-w-sm border border-gray-700/60 backdrop-blur-md">
              {/* Mic Icon Avatar Header */}
              <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-lg ring-4 ring-emerald-500/20">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              </div>

              {/* Title / Timestamp info */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-200 truncate max-w-[280px]">
                  {currentMedia.fileName || 'Voice Message'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {currentMedia.time || 'Audio Message'}
                </p>
              </div>

              {/* Custom Styled Audio Player */}
              <div className="w-full bg-[#111b21] rounded-full p-2 px-4 border border-gray-800 flex items-center gap-3 shadow-inner">
                <audio
                  src={currentMediaUrl}
                  controls
                  className="w-full h-8 accent-emerald-500"
                />
              </div>
            </div>
          ) : isPdf ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center gap-3 text-white">
                  <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-300">
                    Loading PDF module...
                  </p>
                </div>
              }
            >
              <PdfPreview
                url={currentMediaUrl}
                fileName={currentMedia.fileName}
              />
            </Suspense>
          ) : isDocx ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center gap-3 text-white">
                  <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-300">
                    Loading Document module...
                  </p>
                </div>
              }
            >
              <DocxPreview
                url={currentMediaUrl}
                fileName={currentMedia.fileName}
              />
            </Suspense>
          ) : isExcel ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center gap-3 text-white">
                  <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-300">
                    Loading Spreadsheet module...
                  </p>
                </div>
              }
            >
              <SpreadsheetPreview
                url={currentMediaUrl}
                fileName={currentMedia.fileName}
              />
            </Suspense>
          ) : isCsv ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center gap-3 text-white">
                  <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-300">
                    Loading CSV module...
                  </p>
                </div>
              }
            >
              <CsvPreview
                url={currentMediaUrl}
                fileName={currentMedia.fileName}
              />
            </Suspense>
          ) : isText ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center gap-3 text-white">
                  <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-300">
                    Loading Text module...
                  </p>
                </div>
              }
            >
              <TextPreview
                url={currentMediaUrl}
                fileName={currentMedia.fileName}
              />
            </Suspense>
          ) : (
            <div className="p-8 bg-[#202c33] rounded-2xl flex flex-col items-center gap-4 text-white">
              <span className="text-sm font-medium text-center">
                Preview not available
              </span>
              <span className="text-xs text-gray-400 text-center">
                {currentMedia.fileName || 'Attachment'}
              </span>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs font-semibold"
              >
                Download File
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Thumbnails Carousel Bar */}
      {mediaList.length > 1 && (
        <div
          ref={carouselRef}
          style={{
            paddingLeft: 'calc(50% - 28px)',
            paddingRight: 'calc(50% - 28px)',
          }}
          className="wa-media-viewer-thumbs bg-[#111b21] border-t border-gray-800/60 py-3 flex items-center gap-2 overflow-x-auto custom-scrollbar no-scrollbar"
        >
          {mediaList.map((item, idx) => {
            const isThumbImage =
              item?.mimeType?.startsWith('image/') ||
              item?.messageType === 'image';
            const isThumbVideo =
              item?.mimeType?.startsWith('video/') ||
              item?.messageType === 'video';
            const isThumbAudio =
              item?.mimeType?.startsWith('audio/') ||
              item?.messageType === 'audio';

            return (
              <button
                key={idx}
                ref={(el) => (thumbnailRefs.current[idx] = el)}
                onClick={() => {
                  setZoom(1);
                  setCurrentIndex(idx);
                }}
                className={`relative w-14 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-all bg-white ${
                  idx === currentIndex
                    ? 'border-emerald-500 scale-105 opacity-100 ring-2 ring-emerald-500/40'
                    : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                {isThumbImage ? (
                  <>
                    <img
                      src={resolveWhatsAppMediaUrl(item?.mediaUrl || '')}
                      alt=""
                      className="w-full h-full object-cover bg-white"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextElementSibling) {
                          e.target.nextElementSibling.style.display = 'flex';
                        }
                      }}
                    />
                    <div
                      style={{ display: 'none' }}
                      className="w-full h-full bg-[#202c33] items-center justify-center text-emerald-400"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </>
                ) : isThumbVideo ? (
                  <div className="w-full h-full bg-[#202c33] flex items-center justify-center text-amber-400">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                ) : isThumbAudio ? (
                  <div className="w-full h-full bg-[#202c33] flex items-center justify-center text-amber-400">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    </svg>
                  </div>
                ) : (
                  <div className="w-full h-full bg-[#202c33] flex items-center justify-center text-gray-300 text-[10px]">
                    DOC
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (isWhatsAppInbox && typeof document !== 'undefined') {
    return createPortal(viewer, document.body);
  }

  return viewer;
}
